import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/auth";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "./OtpModal.css";
import { toast } from "react-toastify";

const CodeInput = ({
  length = 6,
  value,
  onChange,
  onFocus,
  hasError = false,
}) => {
  const inputRef = useRef(null);
  const digits = value.split("").concat(Array(length - value.length).fill(""));

  const handlePress = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    onFocus && onFocus();
  };

  return (
    <div className="otp_modal_code_input_container">
      <div className="otp_modal_code_container" onClick={handlePress}>
        {digits.map((digit, index) => (
          <div
            key={index}
            className={`otp_modal_code_box ${
              value.length === index ? "otp_modal_active_border" : ""
            } ${hasError ? "otp_modal_error_border" : ""}`}
          >
            <div className="otp_modal_code_digit">{digit}</div>
          </div>
        ))}
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            const cleanedText = e.target.value
              .replace(/[^0-9]/g, "")
              .slice(0, length);
            onChange(cleanedText);
          }}
          className="otp_modal_hidden_input"
          type="tel"
          pattern="[0-9]*"
          inputMode="numeric"
          maxLength={length}
          onFocus={onFocus}
          autoFocus
        />
      </div>
    </div>
  );
};

const OtpModal = ({
  visible,
  email,
  phoneNumber,
  userId,
  method = "email",
  onClose,
  onVerify,
  onResend,
  onEditEmail,
  onEditPhone,
  title,
  verifyButtonText = "Proceed",
  verifyButtonColor = "#006400",
  codeLength = 6,
  resendCountdown = 60,
  pendingInterestData, 
}) => {
  const location = useLocation();
  const modalRef = useRef(null);
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [verificationMethod, setVerificationMethod] = useState(method);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (visible && pendingInterestData) {
    }
  }, [visible, pendingInterestData]);

  useEffect(() => {
    if (!visible) {
      setCode("");
      setHasError(false);
      setErrorMessage("");
      setCountdown(0); 
    } else {
      setVerificationMethod(method);
    }
  }, [visible, method]);

  useEffect(() => {
    let timer;
    if (visible && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      clearInterval(timer);
    };
  }, [visible, countdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible, onClose]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [visible, onClose]);

  const handleVerify = async () => {
    if (code.length !== codeLength) {
      setHasError(true);
      setErrorMessage(`Please enter all ${codeLength} digits`);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      await onVerify(code, verificationMethod);
    } catch (error) {
      setHasError(true);
      setErrorMessage(
        error.message || "Verification failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsSending(true);
    setHasError(false);
    setErrorMessage("");

    try {
      await onResend(verificationMethod);
      setCode("");
      setCountdown(resendCountdown);
      toast.success("New code sent successfully");
    } catch (error) {
      setHasError(true);
      setErrorMessage("Failed to resend code. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleMethodChange = async (newMethod) => {
    if (newMethod === verificationMethod || isSending) return;

    setIsSending(true);
    setVerificationMethod(newMethod);

    try {
      if (newMethod === "sms") {
        if (!phoneNumber) {
          throw new Error("Missing phone number for SMS verification");
        }

        const response = await axios.post("/sms/send-otp", {
          phoneNumber,
          textMessage: "verification message",
          userId,
        });

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to send SMS OTP");
        }

        alert("A verification code has been sent to your phone number.");
      } else if (newMethod === "email") {
        if (!email) {
          throw new Error("Missing email for verification");
        }
        await onResend("email");
      }

      setCode("");
      setCountdown(resendCountdown);
    } catch (error) {
      setHasError(true);
      setErrorMessage(error.message || "Failed to change verification method");
      setVerificationMethod(method);
    } finally {
      setIsSending(false);
    }
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" + secs : secs}`;
  };

  const getVerificationTitle = () => {
    if (title) return title;

    if (verificationMethod === "email" && email) {
      const maskedEmail = email.replace(
        /(\w{3})[\w.-]+@([\w.]+)/,
        "$1******@$2",
      );
      return `Enter the ${codeLength}-digit code sent to ${maskedEmail}`;
    }

    if (verificationMethod === "sms" && phoneNumber) {
      let formattedPhone = phoneNumber;
      if (phoneNumber.startsWith("+")) {
        formattedPhone = `${phoneNumber.slice(0, 4)} ${phoneNumber.slice(
          4,
          7,
        )}***${phoneNumber.slice(-4)}`;
      } else if (phoneNumber.length > 6) {
        formattedPhone = `${phoneNumber.slice(0, 4)} *** ${phoneNumber.slice(
          -4,
        )}`;
      }
      return `Enter the ${codeLength}-digit code sent to ${formattedPhone}`;
    }

    return `Enter the ${codeLength}-digit verification code`;
  };

  if (!visible) return null;

  return (
    <div className="otp_modal_overlay">
      <div className="otp_modal_container" ref={modalRef}>
        <div className="otp_modal_header">
          <button className="otp_modal_close_button" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="otp_modal_content">
          <div className="otp_modal_title">{getVerificationTitle()}</div>

          {pendingInterestData && (
            <div className="otp_modal_event_context">
              <div className="otp_modal_event_icon">🎉</div>
              <div className="otp_modal_event_text">
                Registering for:{" "}
                <strong>{pendingInterestData.eventTitle}</strong>
              </div>
            </div>
          )}

          <div className="otp_modal_edit_container">
            <div className="otp_modal_edit_text">
              Incorrect{" "}
              {verificationMethod === "email" ? "email" : "phone number"}?
            </div>
            <button
              className="otp_modal_edit_link"
              onClick={
                verificationMethod === "email" ? onEditEmail : onEditPhone
              }
            >
              Edit
            </button>
          </div>

          {/* <div className="method-selection-container">
            <button
              className={`method-button ${
                verificationMethod === "email" ? "active-method-button" : ""
              }`}
              onClick={() => handleMethodChange("email")}
              disabled={isSending}
            >
              <span className="method-button-text active-method-button-text">
                {verificationMethod === "sms"
                  ? "Use Email"
                  : "Use Phone Number"}
              </span>
            </button>
          </div> */}

          <CodeInput
            length={codeLength}
            value={code}
            onChange={setCode}
            onFocus={() => {
              if (hasError) {
                setHasError(false);
                setErrorMessage("");
              }
            }}
            hasError={hasError}
          />

          <div className="otp_modal_resend_container">
            <span className="otp_modal_resend_text">Didn't get code?</span>
            <button
              className="otp_modal_resend_link"
              onClick={handleResend}
              disabled={countdown > 0 || isSending}
            >
              {countdown > 0
                ? formatTimer(countdown)
                : isSending
                  ? "Sending..."
                  : "Resend Code"}
            </button>
          </div>

          {/* <div className="otp_modal_resend_container">
            <span className="otp_modal_resend_text">Didn't get code?</span>
            <button className="otp_modal_resend_link" onClick={handleResend}>
              Resend Code
            </button>
          </div> */}

          {hasError && (
            <div className="otp_modal_error_message">{errorMessage}</div>
          )}

          <button
            className={`otp_modal_verify_button ${
              code.length !== codeLength || isLoading
                ? "otp_modal_verify_button_disabled"
                : ""
            }`}
            onClick={handleVerify}
            disabled={code.length !== codeLength || isLoading}
          >
            {isLoading ? (
              <div className="otp_modal_loading_spinner" />
            ) : (
              verifyButtonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtpModal;